import { FunctionComponent } from 'react';
import { Dialog, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { ButtonFlatLarge } from '@/design-system';
import { css } from '@emotion/css';
import colors from '@peachjar/ui/dist/lib/styles/colors';

type Props = {
  isOpen: boolean;
  schoolName: string;
  submitText?: string;
  onSubmit: () => void;
  onCancel: () => void;
};

const DIALOG_TITLE_ID = 'child-removal-confirmation-title';

const ChildRemovalConfirmation: FunctionComponent<Props> = ({
  isOpen,
  schoolName,
  submitText = 'Remove',
  onSubmit,
  onCancel,
}) => (
  <Dialog
    open={isOpen}
    onClose={onCancel}
    maxWidth="xs"
    fullWidth
    aria-labelledby={DIALOG_TITLE_ID}
    PaperProps={{ 'aria-modal': true }}
  >
    <div className={styles.header}>
      <h1 id={DIALOG_TITLE_ID} className={styles.headerText}>
        Child Removal Confirmation
      </h1>
    </div>
    <DialogContent className={styles.content}>
      <DialogContentText>
        <div className={styles.text}>
          <p>
            Removing this child from your account will:
            <ul>
              <li>
                1. Result in all <span className={styles.schoolName}> {schoolName}'s</span> flyers
                no longer being associated with your account
              </li>
              <li>
                2. Stop email notifications of new flyers sent from
                <span className={styles.schoolName}> {schoolName} </span>
              </li>
            </ul>
          </p>
          <p>
            If you would like to continue viewing flyers from
            <span className={styles.schoolName}> {schoolName} </span>
            but wish to stop receiving email notifications,{' '}
            <a href="/account/preference/notifications" className={styles.link}>
              turn off email notifications
            </a>{' '}
            instead of removing the child.
          </p>
          <p>
            If this child is now attending a different school,
            <a href="#" onClick={onCancel} className={styles.link} role="button">
              update their school information
            </a>{' '}
            to view flyers from their new school instead of removing the child.
          </p>
        </div>
      </DialogContentText>
    </DialogContent>
    <DialogActions className={styles.footer}>
      <ButtonFlatLarge
        onClick={() => onCancel()}
        data-testid="removeChildConfirmationCancelButton"
        autoFocus
      >
        Cancel
      </ButtonFlatLarge>
      <ButtonFlatLarge onClick={() => onSubmit()} data-testid="removeChildConfirmationSubmitButton">
        {submitText}
      </ButtonFlatLarge>
    </DialogActions>
  </Dialog>
);

const styles = {
  header: css`
    background-color: #ffebe5;
    padding: 16px 24px;
  `,
  headerText: css`
    display: flex;
    justify-content: space-between;
    color: ${colors.prussian};
    font-size: 1.5625rem;
    font-family: 'Proxima Nova', Arial, Helvetica, sans-serif;
    font-weight: 500;
    margin: 0;
  `,
  content: css`
    padding: 16px 16px 0px 16px !important;
  `,
  text: css`
    font-family: 'Proxima Nova', Helvetica, Arial, sans-serif;
    font-size: 16px;
    line-height: 24px;
    padding: 8px;
    color: ${colors.prussian};
    ul {
      padding-top: 4px;
      padding-left: 16px;
    }

    li {
      list-style-type: none;
      padding-bottom: 8px;
    }
  `,
  schoolName: css`
    font-weight: bold;
    text-transform: capitalize;
  `,
  link: css`
    color: ${colors.jungle};
    text-decoration: underline;
    font-family: 'Proxima Nova', Helvetica, Arial, sans-serif;
    cursor: pointer;

    &:hover {
      font-weight: bold;
    }
  `,
  footer: css`
    padding: 0px 16px 8px 16px !important;
  `,
};

export default ChildRemovalConfirmation;
